import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { adminGuard } from './guards/admin.guard';
import { advisorGuard } from './guards/advisor.guard';
import { lenderGuard } from './guards/lender.guard';
import { emailVerifiedGuard } from './guards/email-verified.guard';
import { subscriptionGuard } from './guards/subscription.guard';

// Eagerly loaded: landing page and auth flow (critical path)
// Home page is v3. Rollback: import HomeComponent from './pages/v2/homeV2/home-v2.component',
// use it for the '' route, and drop `data: { [HIDE_SHELL]: true }` (v2 relies on the global navbar/footer).
import { HomeV3Component } from './pages/v3/home/home-v3.component';
import { HIDE_SHELL } from './core/shell';
// /auth is v3; rollback: route AuthComponent ('./pages/v2/auth-v2/auth-v2.component') without hideShell.
import { AuthPageV3Component } from './pages/v3/auth/auth-page.component';

export const routes: Routes = [
  // ==========================================
  // Main Routes
  // ==========================================
  { 
    path: '', 
    component: HomeV3Component,
    data: { [HIDE_SHELL]: true },
    title: 'creditpath.io - Smart Financial Solutions'
  },
  { 
    path: 'auth',
    component: AuthPageV3Component,
    data: { [HIDE_SHELL]: true },
    title: 'Log In - creditpath.io'
  },
  { 
    path: 'forgot-password',
    // v3; rollback: route ForgotPasswordComponent ('./pages/v2/forgot-password-v2/forgot-password-v2.component') without hideShell.
    loadComponent: () => import('./pages/v3/forgot-password/forgot-password-v3.component').then(m => m.ForgotPasswordV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Reset Password - creditpath.io'
  },
  { 
    path: 'email-verification',
    // v3; rollback: load EmailVerificationComponent from './pages/v2/email-verification-v2/email-verification-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/email-verification/email-verification-v3.component').then(m => m.EmailVerificationV3Component),
    data: { [HIDE_SHELL]: true },
    canActivate: [authGuard],
    title: 'Verify Email - creditpath.io'
  },
  { 
    path: 'profile-completion',
    // v3; rollback: load ProfileCompletionComponent from './pages/v2/profile-completion-v2/profile-completion-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/profile-completion/profile-completion-v3.component').then(m => m.ProfileCompletionV3Component),
    data: { [HIDE_SHELL]: true },
    canActivate: [emailVerifiedGuard],
    title: 'Complete Profile - creditpath.io'
  },
  { 
    path: 'auth/action',
    // v3; rollback: route AuthActionComponent ('./pages/v2/auth-action-v2/auth-action-v2.component') without hideShell.
    loadComponent: () => import('./pages/v3/auth-action/auth-action-v3.component').then(m => m.AuthActionV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Email Verification - creditpath.io'
  },
  {
    path: 'privacy-policy',
    // v3; rollback: load PrivacyPolicyComponent from './pages/v2/privacy-policy-v2/privacy-policy-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/legal/privacy-policy-v3.component').then(m => m.PrivacyPolicyV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Privacy Policy - creditpath.io'
  },
  {
    path: 'terms-and-conditions',
    // v3; rollback: load TermsAndConditionsComponent from './pages/v2/terms-and-conditions-v2/terms-and-conditions-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/legal/terms-v3.component').then(m => m.TermsV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Terms and Conditions - creditpath.io'
  },
  { 
    path: 'request-terms',
    // v3; rollback: load RequestTermsComponent from './pages/v2/request-terms-v2/request-terms-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/request-terms/request-terms-v3.component').then(m => m.RequestTermsV3Component),
    canActivate: [emailVerifiedGuard, advisorGuard, subscriptionGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Request Terms - creditpath.io'
  },
  { 
    path: 'book-of-business-request', 
    redirectTo: 'request-terms',
    pathMatch: 'full'
  },
  {
    path: 'term-request-details/:id',
    // v3; rollback: load TermRequestDetailsComponent from './pages/v2/term-request-details-v2/term-request-details-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/request/advisor-request-page.component').then(m => m.AdvisorRequestPageV3Component),
    canActivate: [emailVerifiedGuard, advisorGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Term Request Details - creditpath.io'
  },
  {
    path: 'submit-offer/:id',
    // v3; rollback: load SubmitOfferComponent from './pages/v2/submit-offer-v2/submit-offer-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/request/submit-offer-page.component').then(m => m.SubmitOfferPageV3Component),
    canActivate: [authGuard, lenderGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Submit Offer - creditpath.io'
  },
  {
    path: 'lender-portal',
    // v3; rollback: load LenderPortalComponent from './pages/v2/lender-portal-v2/lender-portal-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/lender-portal/lender-portal-v3.component').then(m => m.LenderPortalV3Component),
    canActivate: [authGuard, lenderGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Lender Portal - creditpath.io'
  },
  {
    path: 'advisor-portal',
    // v3; rollback: load AdvisorPortalComponent from './pages/v2/advisor-portal-v2/advisor-portal-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/advisor-portal/advisor-portal-v3.component').then(m => m.AdvisorPortalV3Component),
    canActivate: [emailVerifiedGuard, advisorGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Advisor Portal - creditpath.io'
  },
  {
    path: 'offer-details/:termRequestId',
    // v3; rollback: load OfferDetailsComponent from './pages/v2/offer-details-v2/offer-details-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/request/lender-request-page.component').then(m => m.LenderRequestPageV3Component),
    canActivate: [authGuard, lenderGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Offer Details - creditpath.io'
  },
  {
    path: 'user-settings',
    // v3; rollback: load UserSettingsComponent from './pages/v2/user-settings-v2/user-settings-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/settings/settings-v3.component').then(m => m.SettingsV3Component),
    canActivate: [authGuard],
    data: { [HIDE_SHELL]: true },
    title: 'User Settings - creditpath.io'
  },
  {
    path: 'admin/invite-lender',
    // v3; rollback: load AdminLenderInviteComponent from './pages/v2/admin-lender-invite-v2/admin-lender-invite-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/admin-invite/admin-invite-v3.component').then(m => m.AdminInviteV3Component),
    data: { [HIDE_SHELL]: true },
    canActivate: [authGuard, adminGuard],
    title: 'Invite Lender - creditpath.io'
  },
  {
    path: 'lender-signup',
    // v3; rollback: route LenderSignupComponent ('./pages/v2/lender-signup-v2/lender-signup-v2.component') without hideShell.
    loadComponent: () => import('./pages/v3/lender-signup/lender-signup-v3.component').then(m => m.LenderSignupV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Lender Signup - creditpath.io'
  },

  // ==========================================
  // Document viewer
  // ==========================================
  {
    path: 'documents/:docId',
    // v3; rollback: load DocumentViewerComponent from './pages/v2/document-viewer/document-viewer.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/document-viewer/document-viewer-v3.component').then(m => m.DocumentViewerV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Document - creditpath.io',
  },

  // ==========================================
  // Standalone pages
  // ==========================================
  { 
    path: 'pricing',
    // v3; rollback: load PricingPageComponent from './pages/v2/pricing-page-v2/pricing-page-v2.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/pricing/pricing-page-v3.component').then(m => m.PricingPageV3Component),
    canActivate: [authGuard],
    data: { [HIDE_SHELL]: true },
    title: 'Choose Your Plan - creditpath.io'
  },
  {
    path: 'about',
    loadComponent: () => import('./pages/v3/about/about-page.component').then(m => m.AboutPageV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'About - creditpath.io'
  },
  {
    path: 'contact',
    loadComponent: () => import('./pages/v3/contact/contact-page.component').then(m => m.ContactPageV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Contact Us - creditpath.io'
  },

  // ==========================================
  // Legacy V2 path redirects (for backwards compatibility)
  // ==========================================
  { path: 'v2/home', redirectTo: '', pathMatch: 'full' },
  { path: 'v2/auth', redirectTo: 'auth', pathMatch: 'full' },
  { path: 'v2/forgot-password', redirectTo: 'forgot-password', pathMatch: 'full' },
  { path: 'v2/email-verification', redirectTo: 'email-verification', pathMatch: 'full' },
  { path: 'v2/auth/action', redirectTo: 'auth/action', pathMatch: 'full' },
  { path: 'v2/profile-completion', redirectTo: 'profile-completion', pathMatch: 'full' },
  { path: 'v2/user-settings', redirectTo: 'user-settings', pathMatch: 'full' },
  { path: 'v2/request-terms', redirectTo: 'request-terms', pathMatch: 'full' },
  { path: 'v2/term-request-details/:id', redirectTo: 'term-request-details/:id', pathMatch: 'full' },
  { path: 'v2/offer-details/:termRequestId', redirectTo: 'offer-details/:termRequestId', pathMatch: 'full' },
  { path: 'v2/lender-signup', redirectTo: 'lender-signup', pathMatch: 'full' },
  { path: 'v2/lender-portal', redirectTo: 'lender-portal', pathMatch: 'full' },
  { path: 'v2/submit-offer/:id', redirectTo: 'submit-offer/:id', pathMatch: 'full' },
  { path: 'v2/advisor-portal', redirectTo: 'advisor-portal', pathMatch: 'full' },
  { path: 'v2/book-of-business-request', redirectTo: 'book-of-business-request', pathMatch: 'full' },
  { path: 'v2/admin/invite-lender', redirectTo: 'admin/invite-lender', pathMatch: 'full' },
  { path: 'v2/privacy-policy', redirectTo: 'privacy-policy', pathMatch: 'full' },
  { path: 'v2/terms-and-conditions', redirectTo: 'terms-and-conditions', pathMatch: 'full' },

  // ==========================================
  // 404 Not Found
  // ==========================================
  { 
    path: '**', 
    // v3; rollback: load NotFoundComponent from './pages/v2/not-found/not-found.component' and drop hideShell.
    loadComponent: () => import('./pages/v3/not-found/not-found-v3.component').then(m => m.NotFoundV3Component),
    data: { [HIDE_SHELL]: true },
    title: 'Page Not Found - creditpath.io'
  }
];
